import { computed, reactive, ref } from "vue";
import { useFormDraft } from "@/composables/useFormDraft";

export function useVenaScrapForm() {
  const getToday = () => {
    const d = new Date();
    const year  = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day   = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Un id único por intento de envío: viaja con el payload como
  // "idempotency_key" para que el backend pueda reconocer un reintento
  // (botón doble-tap, recarga de página tras un error de red, etc.) y
  // devolver el registro ya creado en vez de duplicarlo.
  function generarIdempotencyKey() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `vs-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  const initialState = {
    fecha: getToday(),
    operador: "",
    operadorLabel: "",
    maquina: "",
    maquinaLabel: "",
    producto: "",
    productoLabel: "",
    color: "",
    colorLabel: "",
    colorHex: "",
    aditivo: false,
    venaKg: "",
    scrapKg: "",
    comentarios: "",
    folio: "",
    registeredAt: null,
  };

  const form = reactive({ ...initialState, idempotencyKey: generarIdempotencyKey() });

  const currentStep = ref(1);
  const { completeDraft, startNewDraft } = useFormDraft({
    key: "divisan:draft:vena-scrap",
    form,
    currentStep,
    version: 1,
    maxStep: 4,
  });

  const canGoBack = computed(() => {
    return currentStep.value > 1 && currentStep.value < 5;
  });

  function parseNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatKg(value) {
    return parseNumber(value).toFixed(2);
  }

  function makeFolio() {
    const n = new Date();
    const p = (v) => String(v).padStart(2, "0");
    return `#DIV-${n.getFullYear()}-${Math.floor(1000 + Math.random() * 8999)}`;
  }

  function validateStep(step = currentStep.value) {
    if (step === 1) {
      return Boolean(form.fecha && form.operador && form.maquina);
    }

    if (step === 2) {
      return Boolean(form.producto && form.color);
    }

    if (step === 3) {
      const venaValida  = form.venaKg  !== "" && parseNumber(form.venaKg)  >= 0;
      const scrapValida = form.scrapKg !== "" && parseNumber(form.scrapKg) >= 0;

      // Al menos uno debe tener valor
      return venaValida || scrapValida;
    }

    return true;
  }

  function buildPayload() {
    return {
      tipo_reporte: "vena_scrap",
      fecha_registro: new Date().toISOString(),
      fecha: form.fecha,
      id_operador: form.operador,
      id_maquina: form.maquina,
      id_producto: form.producto,
      id_color: form.color,
      aditivo: Boolean(form.aditivo),
      venaKg: parseNumber(form.venaKg),
      scrapKg: parseNumber(form.scrapKg),
      comentarios: form.comentarios || "",
      idempotency_key: form.idempotencyKey,
    };
  }

  function next() {
    if (currentStep.value === 4) {
      form.folio = makeFolio();
      form.registeredAt = new Date();
    }
    if (currentStep.value < 5) {
      currentStep.value++;
    }
  }

  function back() {
    if (currentStep.value > 1) {
      currentStep.value--;
    }
  }

  function resetForm() {
    Object.assign(form, {
      ...initialState,
      fecha: getToday(),
      idempotencyKey: generarIdempotencyKey(),
    });
    currentStep.value = 1;
    startNewDraft();
  }

  return {
    form,
    currentStep,
    canGoBack,
    parseNumber,
    formatKg,
    validateStep,
    buildPayload,
    next,
    back,
    resetForm,
    completeDraft,
  };
}
